// Video preview shared by the single-post composer and every batch row.
// TikTok's Direct Post guidelines (§5) require the creator to see the content
// before it is sent, and §1c requires the clip to be validated against the
// account's `max_video_post_duration_sec` — both of which need a real <video>.

import { useEffect, useState } from "react";

/** Create an object URL for `file` and revoke it when the file changes or the
 * component unmounts. One URL per mounted preview, created and revoked by the
 * same effect, so a 37-video import frees all 37 blobs the moment the batch
 * panel goes away — there is no other place object URLs are minted. */
function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      // Drop the now-dead URL from state too, so a re-render can never point a
      // <video> at a revoked blob.
      setUrl(null);
    };
  }, [file]);
  return url;
}

const IMAGE_EXT = /\.(jpe?g|png|webp|gif)$/i;

/** Whether this file should be shown as a still rather than played. An image in
 * a <video> is a black box that never fires `loadedmetadata`, so it would render
 * as a dead player with no duration — worse than no preview at all. Some folder
 * pickers hand back an empty `type`, so fall back to the extension. */
function isImageFile(file: File): boolean {
  return file.type ? file.type.startsWith("image/") : IMAGE_EXT.test(file.name);
}

/** "2m 05s" / "48s" — short enough to sit inline next to the player. */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return mins > 0 ? `${mins}m ${String(secs).padStart(2, "0")}s` : `${secs}s`;
}

export function VideoPreview({
  file = null,
  src = "",
  maxDurationSec = null,
  onDuration,
}: {
  /** Local file (folder import, or a just-picked upload) — preferred source. */
  file?: File | null;
  /** Remote fallback, used when there is no local file (e.g. a pasted URL). */
  src?: string;
  /** TikTok's per-account limit, or null when TikTok isn't a target — and also
   * null when the account has no usable limit. Callers MUST resolve it through
   * `tiktokMaxDurationSec`, which is what turns an absent/0 limit into null;
   * a non-null value here is always a real limit worth flagging against. */
  maxDurationSec?: number | null;
  /** Reports the measured duration up so the caller can gate publishing. */
  onDuration?: (seconds: number) => void;
}) {
  const objectUrl = useObjectUrl(file);
  // Remember which source a measurement belongs to: swapping the video makes the
  // previous duration stale, and pairing them means we never have to reset it.
  const [measured, setMeasured] = useState<{ url: string; seconds: number } | null>(null);

  const url = objectUrl ?? src;
  if (!url) return null;

  // An image post still gets a preview (§5a is about seeing the content), just
  // not a player: there is nothing to play and no duration to measure.
  if (file && isImageFile(file)) {
    return (
      <div className="media-preview">
        <img className="media-preview-image" src={url} alt="" />
      </div>
    );
  }

  const seconds = measured?.url === url ? measured.seconds : null;
  // The limit only when it is actually exceeded — carrying the number (rather
  // than a boolean) keeps it non-null for the message below.
  const exceeded =
    seconds !== null && maxDurationSec !== null && seconds > maxDurationSec ? maxDurationSec : null;

  return (
    <div className="media-preview">
      <video
        className="media-preview-video"
        src={url}
        controls
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => {
          const value = e.currentTarget.duration;
          if (!Number.isFinite(value) || value <= 0) return;
          setMeasured({ url, seconds: value });
          onDuration?.(value);
        }}
      />
      {seconds !== null && (
        <p className={exceeded !== null ? "error small" : "muted small"}>
          {exceeded !== null
            ? `Too long for TikTok — ${formatDuration(seconds)} exceeds this account's ${formatDuration(exceeded)} limit.`
            : formatDuration(seconds)}
        </p>
      )}
    </div>
  );
}
